import get from "lodash/get";

const METADATA_FIELDS = [
  { key: "primaryMuscleGroup", labelKey: "extraInput.primaryMuscleGroup" },
  { key: "secondaryMuscleGroup", labelKey: "extraInput.secondaryMuscleGroup" },
  { key: "exerciseTypeField", labelKey: "extraInput.exerciseType" },
  { key: "video", labelKey: "exerciseLibraryScreen.video" },
  { key: "instructions", labelKey: "extraInput.instructions" },
];

const EMPTY_SENTINELS = new Set(["", "null", "none", "undefined"]);

/** `body.source` for library POST from Add Exercise (no flash / no refreshWith). */
export const LIBRARY_METADATA_SOURCE_ADD_EXERCISE = "add-exercise";

/** @param {unknown} value */
function isEmptyMetadataString(value) {
  if (value == null) {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return !normalized || EMPTY_SENTINELS.has(normalized);
}

/**
 * Video is present when there is a URL and/or an uploaded file on the record or in the draft.
 *
 * @param {{ videoUrl?: string, videoFile?: unknown, videoOriginal?: string }} values
 */
function hasDraftVideo(values) {
  if (!values) {
    return false;
  }
  return (
    !isEmptyMetadataString(values.videoUrl) ||
    Boolean(values.videoFile) ||
    !isEmptyMetadataString(values.videoOriginal)
  );
}

/** @param {unknown} value */
function hasExerciseLibraryMetadataValue(value) {
  if (value == null) {
    return false;
  }
  if (typeof value === "string") {
    return !isEmptyMetadataString(value);
  }
  return false;
}

/**
 * @param {ReturnType<typeof libraryMetadataValuesFromRecord>} values
 * @returns {string[]}
 */
export function getMetadataMissingKeys(values) {
  if (!values) {
    return METADATA_FIELDS.map((field) => field.key);
  }
  return METADATA_FIELDS.filter((field) => {
    if (field.key === "video") {
      return !hasDraftVideo(values);
    }
    return !hasExerciseLibraryMetadataValue(values[field.key]);
  }).map((field) => field.key);
}

/** Stable string for comparing missing-key lists (avoids effect loops on new array refs). */
export function metadataMissingKeysSignature(keys) {
  return (keys || []).join("\0");
}

/** Compare two missing-key lists.
 * @param {string[]|undefined} a
 * @param {string[]|undefined} b
 */
export function areMetadataMissingKeysEqual(a, b) {
  return metadataMissingKeysSignature(a) === metadataMissingKeysSignature(b);
}

/**
 * @param {object|null|undefined} record — library movement or `primaryExercises[primary]`.
 * @returns {{ complete: boolean, missingKeys: string[] }}
 */
export function getExerciseLibraryMetadataGaps(record) {
  if (!record) {
    return {
      complete: false,
      missingKeys: METADATA_FIELDS.map((f) => f.key),
    };
  }
  const missingKeys = getMetadataMissingKeys(
    libraryMetadataValuesFromRecord(record)
  );
  return {
    complete: missingKeys.length === 0,
    missingKeys,
  };
}

/**
 * Movement name for library lookup from add-exercise form state.
 * @param {object} [form]
 */
function movementNameFromForm(form) {
  if (!form) {
    return null;
  }
  if (typeof form.exerciseMovement === "string" && form.exerciseMovement.trim()) {
    return form.exerciseMovement.trim();
  }
  if (typeof form.secondary === "string" && form.secondary.trim()) {
    return form.secondary.trim();
  }
  return null;
}

/**
 * Resolve metadata record from current form selection (not stale React state).
 *
 * @param {object|null|undefined} activeUserToRequest
 * @param {{ form?: object }} ctx
 */
export function resolveExerciseLibraryMetadataRecord(
  activeUserToRequest,
  { form } = {}
) {
  const { type, primary } = form || {};
  const movement = movementNameFromForm(form);

  if (type === "primary" && primary) {
    return (
      get(activeUserToRequest, ["primaryExercises", primary], null) || {}
    );
  }

  if (primary && movement) {
    const library = activeUserToRequest?.exerciseLibrary?.[primary];
    if (Array.isArray(library)) {
      return library.find((item) => item.movement === movement) || null;
    }
  }

  return null;
}

/** Library-backed add-exercise selections that can surface metadata gaps. */
export function isLibraryBackedExerciseSelection(form) {
  const type = form?.type;
  return type === "secondary" || type === "primary" || type === "assistant";
}

/**
 * Stable id for the current library-backed exercise selection.
 * Used as React `key` on the metadata panel so it fully resets when the coach
 * picks another exercise (collapsed, fresh draft).
 *
 * @param {object} [form]
 * @returns {string}
 */
export function libraryMetadataSelectionKey(form) {
  return [
    form?.type || "",
    form?.primary || "",
    movementNameFromForm(form) || "",
  ].join("\0");
}

/**
 * Whether the "Exercise details" panel shows for the current selection: always for
 * library-backed exercises, so the video preview is visible even when nothing is missing
 * (`missingKeys` is then empty and the "N missing" badge is hidden).
 *
 * @param {object|null|undefined} activeUserToRequest
 * @param {{ form?: object, hasExerciseSelection?: boolean }} ctx
 * @returns {{ show: false } | { show: true, missingKeys: string[] }}
 */
export function getLibraryMetadataNoticeState(
  activeUserToRequest,
  { form, hasExerciseSelection } = {}
) {
  if (!hasExerciseSelection || !isLibraryBackedExerciseSelection(form)) {
    return { show: false };
  }
  const record = resolveExerciseLibraryMetadataRecord(activeUserToRequest, {
    form,
  });
  const { missingKeys } = getExerciseLibraryMetadataGaps(record);
  const isPrimaryExercise = form?.type === "primary";
  const libraryIndex = isPrimaryExercise
    ? -1
    : resolveLibraryMovementIndex(activeUserToRequest, { form });
  const { primary } = form || {};
  const repMaximum = isPrimaryExercise
    ? activeUserToRequest?.repMaximums?.[primary] ?? null
    : record?.repMaximum ?? null;
  return {
    show: true,
    missingKeys,
    record,
    libraryIndex,
    isPrimaryExercise,
    repMaximum,
  };
}

/**
 * Index in `exerciseLibrary[primary]` for the current form movement (-1 if not found).
 *
 * @param {object|null|undefined} activeUserToRequest
 * @param {{ form?: object }} ctx
 */
export function resolveLibraryMovementIndex(activeUserToRequest, { form } = {}) {
  const { type, primary } = form || {};
  if (type === "primary" || !primary) {
    return -1;
  }
  const movement = movementNameFromForm(form);
  if (!movement) {
    return -1;
  }
  const library = activeUserToRequest?.exerciseLibrary?.[primary];
  if (!Array.isArray(library)) {
    return -1;
  }
  return library.findIndex((item) => item.movement === movement);
}

/** Whether the panel can offer "save to master library" for the current selection. */
export function canSaveLibraryMetadataToMaster({
  formPrimary,
  isPrimaryExercise,
  libraryIndex,
} = {}) {
  if (!formPrimary) {
    return false;
  }
  return Boolean(isPrimaryExercise) || libraryIndex >= 0;
}

/**
 * Snapshot for add-exercise library metadata save (`edit-secondary` or `update-primary`).
 *
 * `shouldSave` is the gate the submit pipeline checks: the library save fires only when the
 * selection can be saved (`canSaveLibraryMetadataToMaster`) AND the coach changed something
 * (`hasAnyLibraryMetadataChanged`). No checkbox — the save is implicit on any edit.
 *
 * @param {{
 *   draft?: ReturnType<typeof libraryMetadataValuesFromRecord>,
 *   record?: object|null,
 *   libraryIndex?: number,
 *   formPrimary?: string,
 *   isPrimaryExercise?: boolean,
 *   repMaximum?: number|null,
 * }} ctx
 */
export function buildLibraryMetadataSavePayload({
  draft,
  record,
  libraryIndex,
  formPrimary,
  isPrimaryExercise,
  repMaximum,
} = {}) {
  const values = draft || libraryMetadataValuesFromRecord(record);
  /*
   shouldSave is the gate the submit pipeline checks: the library save fires only when the
   selection can be saved (`canSaveLibraryMetadataToMaster`) AND the coach changed something
   (`hasAnyLibraryMetadataChanged`). No checkbox — the save is implicit on any edit.
  */
  const shouldSave =
    canSaveLibraryMetadataToMaster({
      formPrimary,
      isPrimaryExercise,
      libraryIndex,
    }) && hasAnyLibraryMetadataChanged(record, draft);
  return {
    shouldSave: Boolean(shouldSave),
    isPrimaryExercise: Boolean(isPrimaryExercise),
    libraryIndex: libraryIndex ?? -1,
    primary: formPrimary || record?.primary || null,
    movement: record?.movement ?? null,
    repMaximum: repMaximum ?? record?.repMaximum ?? null,
    exerciseTypeField: values.exerciseTypeField || "",
    primaryMuscleGroup: values.primaryMuscleGroup || "",
    secondaryMuscleGroup: values.secondaryMuscleGroup || "",
    instructions: values.instructions || "",
    videoUrl: values.videoUrl || "",
    videoOriginal: values.videoOriginal || "",
    // Display path string (not the File). File bytes travel via videoFileRef → files.videoFile.
    videoFilePath: values.videoFile || null,
  };
}

const METADATA_DRAFT_KEYS = [
  "primaryMuscleGroup",
  "secondaryMuscleGroup",
  "exerciseTypeField",
  "instructions",
];

/**
 * True when the draft differs from the library record on any catalog field.
 *
 * Covers any edit (not just filling empty gaps): text fields plus video — a freshly
 * uploaded file (`draft.videoFile`), or a changed URL / library original.
 *
 * @param {object|null|undefined} record
 * @param {ReturnType<typeof libraryMetadataValuesFromRecord>|null|undefined} draft
 */
export function hasAnyLibraryMetadataChanged(record, draft) {
  if (!draft) {
    return false;
  }
  const baseline = libraryMetadataValuesFromRecord(record);
  for (const key of METADATA_DRAFT_KEYS) {
    if ((baseline[key] || "") !== (draft[key] || "")) {
      return true;
    }
  }
  // video: freshly uploaded file, or changed URL / library original
  if (draft.videoFile) {
    return true;
  }
  if ((baseline.videoUrl || "") !== (draft.videoUrl || "")) {
    return true;
  }
  return (baseline.videoOriginal || "") !== (draft.videoOriginal || "");
}

/**
 * Merge server-saved library metadata into the exercise being added.
 *
 * `addExtraPropertiesToExercise` builds the exercise's metadata from the stale in-memory
 * library cache; when the coach just edited it (and saved to master library), the action
 * returns the resolved metadata so THIS exercise reflects it without waiting for a refresh.
 * `video` carries the server-resolved value (incl. the uploaded video URL) and may be `""`
 * (deleted), so it is applied whenever present — `?? ` would drop a meaningful empty string.
 *
 * @param {object} exercise — exercise about to be POSTed to `exercise/save`.
 * @param {object|null|undefined} saved — metadata returned by the library action.
 * @returns {object} exercise with the saved metadata applied (or the original if no `saved`).
 */
export function mergeSavedMetadataIntoExercise(exercise, saved) {
  if (!exercise || !saved) {
    return exercise;
  }
  return {
    ...exercise,
    video: saved.video === undefined ? exercise.video : saved.video,
    instructions: saved.instructions ?? exercise.instructions,
    exerciseTypeField: saved.exerciseTypeField ?? exercise.exerciseTypeField,
    primaryMuscleGroup: saved.primaryMuscleGroup ?? exercise.primaryMuscleGroup,
    secondaryMuscleGroup:
      saved.secondaryMuscleGroup ?? exercise.secondaryMuscleGroup,
  };
}

export function libraryMetadataValuesFromRecord(record) {
  const video = record?.video;
  let videoUrl = "";
  let videoOriginal = "";
  if (video && typeof video === "object") {
    videoUrl = video.url || "";
    videoOriginal = video.videoOriginal || "";
  } else if (typeof video === "string") {
    videoUrl = video;
  }
  return {
    exerciseTypeField: record?.exerciseTypeField || "",
    primaryMuscleGroup: record?.primaryMuscleGroup || "",
    secondaryMuscleGroup: record?.secondaryMuscleGroup || "",
    videoUrl,
    videoFile: null,
    videoOriginal,
    instructions: record?.instructions || "",
  };
}

export { METADATA_FIELDS };
